import React, { useState } from 'react';
import {
  Badge,
  Box,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  Heading,
  Input,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Select,
  SimpleGrid,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
  Stat,
  StatHelpText,
  StatLabel,
  StatNumber,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { clearBest, leads, shownPercent, shownValue } from '../../utils/probabilityDisplay';
import { compareMatchScores, matchScoreLabel } from '../../utils/ttMatchScore';
import {
  ttModelService,
  TtMarketsRequest,
  TtMarketsResponse,
  TtMatchFormat,
  TtMatchScoreOdds,
  TtTwoWayOdds,
} from '../../services/tabletennis/ttModelService';
import { parseConfiguredNumbers } from '../../utils/ttConfiguredNumbers';
import { currentSetNumber, parseSetScores } from '../../utils/ttSetScores';

/** A pair of probabilities and their fair prices, the shape every market's result takes. */
const TwoWayResult: React.FC<{
  labelA: string;
  labelB: string;
  probA: number;
  probB: number;
  fairPriceA: number;
  fairPriceB: number;
}> = ({ labelA, labelB, probA, probB, fairPriceA, fairPriceB }) => (
  <SimpleGrid columns={2} spacing={4}>
    <Stat>
      <StatLabel>{labelA}</StatLabel>
      <StatNumber fontSize="lg" color={leads(probA, probB) ? 'green.600' : 'red.600'}>
        {shownPercent(probA)}%
      </StatNumber>
      <StatHelpText mb={0}>Fair price: {fairPriceA.toFixed(3)}</StatHelpText>
    </Stat>
    <Stat>
      <StatLabel>{labelB}</StatLabel>
      <StatNumber fontSize="lg" color={leads(probB, probA) ? 'green.600' : 'red.600'}>
        {shownPercent(probB)}%
      </StatNumber>
      <StatHelpText mb={0}>Fair price: {fairPriceB.toFixed(3)}</StatHelpText>
    </Stat>
  </SimpleGrid>
);

/**
 * The endings the response priced, read as one book: a player's two ways of winning grouped together,
 * the likeliest picked out. Not a {@link TwoWayResult} — there are up to four of these, so the highlight
 * cannot be a comparison of a pair, and the grid is driven by however many endings are still reachable.
 *
 * <p>Named the way the market card names them, off the same rule, so the two boards can be read against
 * each other: the player who takes the ending, then the score from their side.
 */
const MatchScoreResult: React.FC<{ endings: TtMatchScoreOdds[] }> = ({ endings }) => {
  // Null at every prematch, where four exhaustive endings sit at 25.00% each: nothing is picked out,
  // because nothing the reader can see is ahead.
  const likeliest = clearBest(endings.map(ending => ending.probability));

  return (
    <SimpleGrid columns={{ base: 2, md: endings.length }} spacing={4}>
      {[...endings].sort(compareMatchScores).map(ending => (
        <Stat key={`${ending.setsA}-${ending.setsB}`}>
          <StatLabel>{matchScoreLabel(ending, 'Player A', 'Player B')}</StatLabel>
          <StatNumber
            fontSize="lg"
            color={shownValue(ending.probability) === likeliest ? 'green.600' : 'red.600'}
          >
            {shownPercent(ending.probability)}%
          </StatNumber>
          <StatHelpText mb={0}>Fair price: {ending.fairPrice.toFixed(3)}</StatHelpText>
        </Stat>
      ))}
    </SimpleGrid>
  );
};

/** A handicap line as the market states it: from Player A's side, the sign always shown. */
const handicapLine = (line: number): string => (line > 0 ? `+${line}` : String(line));

const twoWay = (odds: TtTwoWayOdds) => ({
  probA: odds.probA,
  probB: odds.probB,
  fairPriceA: odds.fairPriceA,
  fairPriceB: odds.fairPriceB,
});

export const TtMarketsForm: React.FC = () => {
  const [setScores, setSetScores] = useState<string>('');
  const [pointsA, setPointsA] = useState<number>(0);
  const [pointsB, setPointsB] = useState<number>(0);
  const [format, setFormat] = useState<TtMatchFormat>('BO3');
  const [pointWinProb, setPointWinProb] = useState<number>(0.5);
  const [raceTargetsText, setRaceTargetsText] = useState<string>('');
  const [pointNumbersText, setPointNumbersText] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [result, setResult] = useState<TtMarketsResponse | null>(null);
  // The format the result was priced at, which decides what an empty handicap list means.
  const [pricedFormat, setPricedFormat] = useState<TtMatchFormat>('BO3');
  const [durationMillis, setDurationMillis] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();

  const parsed = parseSetScores(setScores);
  const parsedTargets = parseConfiguredNumbers(raceTargetsText, 'point target');
  const parsedPointNumbers = parseConfiguredNumbers(pointNumbersText, 'point number');
  // A backend older than this market answers without the field; read as no line live rather than broken.
  const setPointsHandicap = result?.setPointsHandicap ?? [];
  const setTotalPoints = result?.setTotalPoints ?? [];
  const setTotalOddEven = result?.setTotalOddEven ?? [];
  // Null once the match is over, and also on a backend older than this market: both read as no line.
  const matchTotalPoints = result?.matchTotalPoints ?? null;
  const matchCorrectScore = result?.matchCorrectScore ?? [];
  const pointNWinner = result?.pointNWinner ?? [];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const inputError = parsed.error || parsedTargets.error || parsedPointNumbers.error;
    if (inputError) {
      setError(inputError);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const request: TtMarketsRequest = {
        completedSets: parsed.sets,
        pointsA,
        pointsB,
        format,
        pointWinProb,
        // Left out unless asked for: an empty list means "price no race", not "use the usual targets".
        ...(parsedTargets.numbers ? { raceTargets: parsedTargets.numbers } : {}),
        ...(parsedPointNumbers.numbers ? { pointNumbers: parsedPointNumbers.numbers } : {}),
      };

      const response = await ttModelService.getMarkets(request);
      setResult(response.value);
      setPricedFormat(format);
      setDurationMillis(response.durationMillis);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to price the markets';
      setError(message);
      setResult(null);
      toast({
        title: 'Error',
        description: message,
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box>
      <Heading size="md" mb={4}>Table Tennis Markets</Heading>
      <Text mb={4}>
        Prices every table tennis market from one score, the way live pricing does — a single
        scoreboard update reprices the whole board. All prices are fair, with no trading margin.
      </Text>

      <Box as="form" onSubmit={handleSubmit}>
        <VStack spacing={3} align="stretch">
          <FormControl isInvalid={!!parsed.error}>
            <FormLabel fontSize="sm">Finished sets</FormLabel>
            <Input
              placeholder="11-7, 8-11"
              value={setScores}
              onChange={(e) => setSetScores(e.target.value)}
            />
            <FormHelperText>
              {parsed.error
                ? parsed.error
                : `Player A's score first, one set per comma. Leave empty for a match that has not started. `
                  + `Set ${currentSetNumber(parsed.sets)} is the one in progress.`}
            </FormHelperText>
          </FormControl>

          <Box>
            <Heading size="xs" mb={2}>Points in the set in progress</Heading>
            <SimpleGrid columns={2} spacing={2}>
              <FormControl isRequired>
                <FormLabel fontSize="sm">Player A</FormLabel>
                <NumberInput min={0} value={pointsA} onChange={(_, value) => setPointsA(value || 0)}>
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>

              <FormControl isRequired>
                <FormLabel fontSize="sm">Player B</FormLabel>
                <NumberInput min={0} value={pointsB} onChange={(_, value) => setPointsB(value || 0)}>
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>
            </SimpleGrid>
          </Box>

          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
            <FormControl isRequired>
              <FormLabel fontSize="sm">Match Format</FormLabel>
              <Select value={format} onChange={(e) => setFormat(e.target.value as TtMatchFormat)}>
                <option value="BO3">Best of 3</option>
                <option value="BO5">Best of 5</option>
              </Select>
              <FormHelperText>Changes the match and parity prices; a single set's price does not depend on it.</FormHelperText>
            </FormControl>

            <FormControl isRequired>
              <FormLabel fontSize="sm">Point Win Probability (Player A): {(pointWinProb * 100).toFixed(1)}%</FormLabel>
              <Slider
                min={0}
                max={1}
                step={0.01}
                value={pointWinProb}
                onChange={(value) => setPointWinProb(value)}
              >
                <SliderTrack>
                  <SliderFilledTrack />
                </SliderTrack>
                <SliderThumb />
              </Slider>
              <NumberInput
                mt={2}
                min={0}
                max={1}
                step={0.01}
                precision={2}
                value={pointWinProb}
                onChange={(_, value) => setPointWinProb(isNaN(value) ? 0 : value)}
              >
                <NumberInputField />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
              <FormHelperText>Live pricing runs at 0.50 for every match; odds move with the score, not this.</FormHelperText>
            </FormControl>
          </SimpleGrid>

          <FormControl isInvalid={!!parsedTargets.error}>
            <FormLabel fontSize="sm">Race targets</FormLabel>
            <Input
              placeholder="5, 7, 9"
              value={raceTargetsText}
              onChange={(e) => setRaceTargetsText(e.target.value)}
            />
            <FormHelperText>
              {parsedTargets.error
                ? parsedTargets.error
                : 'Points a race is run to, one race per set per target. Leave empty for the targets this '
                  + 'environment offers.'}
            </FormHelperText>
          </FormControl>

          <FormControl isInvalid={!!parsedPointNumbers.error}>
            <FormLabel fontSize="sm">Point numbers</FormLabel>
            <Input
              placeholder="5, 10, 15"
              value={pointNumbersText}
              onChange={(e) => setPointNumbersText(e.target.value)}
            />
            <FormHelperText>
              {parsedPointNumbers.error
                ? parsedPointNumbers.error
                : 'Points of a set the point winner is quoted on, one market per set per number. Leave '
                  + 'empty for the numbers this environment offers.'}
            </FormHelperText>
          </FormControl>

          <Button
            type="submit"
            colorScheme="blue"
            isLoading={isLoading}
            isDisabled={!!parsed.error || !!parsedTargets.error || !!parsedPointNumbers.error}
          >
            Price Markets
          </Button>
        </VStack>
      </Box>

      {error && (
        <Text color="red.500" mt={4}>
          {error}
        </Text>
      )}

      {result && (
        <VStack spacing={3} mt={4} align="stretch">
          <Text fontSize="xs" color="gray.600" fontWeight="bold">
            Calculated in {durationMillis}ms — read as sets {result.state.setsA}-{result.state.setsB},{' '}
            {result.state.pointsAlreadyPlayed} points played
          </Text>

          <Box p={3} borderWidth={1} borderRadius="md" bg="blue.50">
            <Heading size="xs" mb={3}>Match Winner</Heading>
            <TwoWayResult labelA="Player A" labelB="Player B" {...twoWay(result.matchWinner)} />
          </Box>

          <Box p={3} borderWidth={1} borderRadius="md" bg="orange.50">
            <Heading size="xs" mb={1}>Match Sets Handicap</Heading>
            <Text fontSize="xs" color="gray.600" mb={3}>
              A line the set score has already settled is left out rather than priced.
            </Text>
            {result.matchSetsHandicap.length === 0 ? (
              <Text fontSize="sm" color="gray.600">
                {pricedFormat === 'BO3'
                  ? 'Both lines are already settled at this score.'
                  : 'Offered on best-of-3 only — ±1.5 is the margin three sets can be won by.'}
              </Text>
            ) : (
              <VStack spacing={3} align="stretch">
                {result.matchSetsHandicap.map(handicap => (
                  <Box key={handicap.line}>
                    <Heading size="xs" mb={2}>Line {handicap.line}</Heading>
                    <TwoWayResult labelA="Player A" labelB="Player B" {...twoWay(handicap)} />
                  </Box>
                ))}
              </VStack>
            )}
          </Box>

          <Box p={3} borderWidth={1} borderRadius="md" bg="yellow.50">
            <Heading size="xs" mb={1}>Match Correct Score</Heading>
            <Text fontSize="xs" color="gray.600" mb={3}>
              The four endings a best-of-3 can have, which are exhaustive — so their probabilities sum
              to one and the four prices are a complete book. An ending the set score has ruled out is
              left out rather than priced, so the board shrinks four → three → two as the sets are taken.
            </Text>
            {matchCorrectScore.length === 0 ? (
              <Text fontSize="sm" color="gray.600">
                {pricedFormat === 'BO3'
                  ? 'The match is decided, so its score is an outcome now rather than a price.'
                  : 'Offered on best-of-3 only — its four endings are the only ones this market enumerates.'}
              </Text>
            ) : (
              <MatchScoreResult endings={matchCorrectScore} />
            )}
          </Box>

          <Box p={3} borderWidth={1} borderRadius="md" bg="purple.50">
            <Heading size="xs" mb={1}>Match Total Points</Heading>
            <Text fontSize="xs" color="gray.600" mb={3}>
              One line on every point the match is played out over, the points already scored included —
              the one closest to an even market at this score. It moves with the score, and one set down
              it moves furthest: from there the match is either this set or this set and another.
            </Text>
            {matchTotalPoints ? (
              <>
                <Heading size="xs" mb={2}>Total {matchTotalPoints.line}</Heading>
                <TwoWayResult
                  labelA="Over"
                  labelB="Under"
                  probA={matchTotalPoints.probOver}
                  probB={matchTotalPoints.probUnder}
                  fairPriceA={matchTotalPoints.fairPriceOver}
                  fairPriceB={matchTotalPoints.fairPriceUnder}
                />
              </>
            ) : (
              <Text fontSize="sm" color="gray.600">
                The match is over, so there is no total left to price — a match total is quoted at every
                live score, whichever set is being played.
              </Text>
            )}
          </Box>

          <Box p={3} borderWidth={1} borderRadius="md" bg="purple.50">
            <Heading size="xs" mb={3}>Match Total Points Odd/Even</Heading>
            <TwoWayResult
              labelA="Odd"
              labelB="Even"
              probA={result.totalOddEven.probOdd}
              probB={result.totalOddEven.probEven}
              fairPriceA={result.totalOddEven.fairPriceOdd}
              fairPriceB={result.totalOddEven.fairPriceEven}
            />
          </Box>

          <Box p={3} borderWidth={1} borderRadius="md" bg="green.50">
            <Heading size="xs" mb={3}>Set Winner</Heading>
            {result.setWinner.length === 0 ? (
              <Text fontSize="sm" color="gray.600">
                No set left to play in this format at this score.
              </Text>
            ) : (
              <VStack spacing={3} align="stretch">
                {result.setWinner.map(set => (
                  <Box key={set.set}>
                    <Heading size="xs" mb={2}>
                      Set {set.set}{' '}
                      <Badge colorScheme={set.live ? 'green' : 'gray'} fontSize="0.6rem">
                        {set.live ? 'in progress' : 'not started'}
                      </Badge>
                    </Heading>
                    <TwoWayResult labelA="Player A" labelB="Player B" {...twoWay(set)} />
                  </Box>
                ))}
              </VStack>
            )}
          </Box>

          <Box p={3} borderWidth={1} borderRadius="md" bg="cyan.50">
            <Heading size="xs" mb={1}>Set Points Handicap</Heading>
            <Text fontSize="xs" color="gray.600" mb={3}>
              One line per set, the one closest to an even market at that set's score — it rolls as the
              set is played. Stated from Player A's side: a negative line is theirs to give away.
            </Text>
            {setPointsHandicap.length === 0 ? (
              <Text fontSize="sm" color="gray.600">
                No line is live at this score — once both players reach 9 the set can only end by two
                points, which every line it offers already decides.
              </Text>
            ) : (
              <Table variant="simple" size="sm">
                <Thead>
                  <Tr>
                    <Th>Set</Th>
                    <Th>Line</Th>
                    <Th isNumeric>Player A</Th>
                    <Th isNumeric>Player B</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {[...setPointsHandicap]
                    .sort((a, b) => a.set - b.set)
                    .map(handicap => (
                      <Tr key={handicap.set}>
                        <Td>
                          {handicap.set}
                          {handicap.live && (
                            <Badge ml={2} colorScheme="green" fontSize="0.6rem">in progress</Badge>
                          )}
                        </Td>
                        <Td>{handicapLine(handicap.line)}</Td>
                        <Td isNumeric color={leads(handicap.probA, handicap.probB) ? 'green.600' : 'red.600'}>
                          {shownPercent(handicap.probA)}%{' '}
                          <Text as="span" color="gray.600">({handicap.fairPriceA.toFixed(3)})</Text>
                        </Td>
                        <Td isNumeric color={leads(handicap.probB, handicap.probA) ? 'green.600' : 'red.600'}>
                          {shownPercent(handicap.probB)}%{' '}
                          <Text as="span" color="gray.600">({handicap.fairPriceB.toFixed(3)})</Text>
                        </Td>
                      </Tr>
                    ))}
                </Tbody>
              </Table>
            )}
          </Box>

          <Box p={3} borderWidth={1} borderRadius="md" bg="purple.50">
            <Heading size="xs" mb={1}>Set Total Points</Heading>
            <Text fontSize="xs" color="gray.600" mb={3}>
              One line per set, the one closest to an even market at that set's score — it rolls as the
              set is played. Neither side belongs to a player: the line is measured against the points
              both of them score together.
            </Text>
            {setTotalPoints.length === 0 ? (
              <Text fontSize="sm" color="gray.600">
                Every set has been played out, so there is no total left to price — a total stays open
                through the whole set, deuce included.
              </Text>
            ) : (
              <Table variant="simple" size="sm">
                <Thead>
                  <Tr>
                    <Th>Set</Th>
                    <Th>Line</Th>
                    <Th isNumeric>Over</Th>
                    <Th isNumeric>Under</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {[...setTotalPoints]
                    .sort((a, b) => a.set - b.set)
                    .map(total => (
                      <Tr key={total.set}>
                        <Td>
                          {total.set}
                          {total.live && (
                            <Badge ml={2} colorScheme="green" fontSize="0.6rem">in progress</Badge>
                          )}
                        </Td>
                        <Td>{total.line}</Td>
                        <Td isNumeric color={leads(total.probOver, total.probUnder) ? 'green.600' : 'red.600'}>
                          {shownPercent(total.probOver)}%{' '}
                          <Text as="span" color="gray.600">({total.fairPriceOver.toFixed(3)})</Text>
                        </Td>
                        <Td isNumeric color={leads(total.probUnder, total.probOver) ? 'green.600' : 'red.600'}>
                          {shownPercent(total.probUnder)}%{' '}
                          <Text as="span" color="gray.600">({total.fairPriceUnder.toFixed(3)})</Text>
                        </Td>
                      </Tr>
                    ))}
                </Tbody>
              </Table>
            )}
          </Box>

          <Box p={3} borderWidth={1} borderRadius="md" bg="purple.50">
            <Heading size="xs" mb={1}>Set Total Points Odd/Even</Heading>
            <Text fontSize="xs" color="gray.600" mb={3}>
              The parity of one set's points, which has no line to roll. A set is left out once both
              players are within two points of the target: from there the set can only end by two, so
              its total can only be even. That set is still being played — it has just gone quiet
              earlier than its own total above, which is quoted through the whole deuce.
            </Text>
            {setTotalOddEven.length === 0 ? (
              <Text fontSize="sm" color="gray.600">
                No set's parity is live at this score — every set is either played out or already close
                enough to its end that only an even total is left.
              </Text>
            ) : (
              <Table variant="simple" size="sm">
                <Thead>
                  <Tr>
                    <Th>Set</Th>
                    <Th isNumeric>Odd</Th>
                    <Th isNumeric>Even</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {[...setTotalOddEven]
                    .sort((a, b) => a.set - b.set)
                    .map(parity => (
                      <Tr key={parity.set}>
                        <Td>
                          {parity.set}
                          {parity.live && (
                            <Badge ml={2} colorScheme="green" fontSize="0.6rem">in progress</Badge>
                          )}
                        </Td>
                        <Td isNumeric color={leads(parity.probOdd, parity.probEven) ? 'green.600' : 'red.600'}>
                          {shownPercent(parity.probOdd)}%{' '}
                          <Text as="span" color="gray.600">({parity.fairPriceOdd.toFixed(3)})</Text>
                        </Td>
                        <Td isNumeric color={leads(parity.probEven, parity.probOdd) ? 'green.600' : 'red.600'}>
                          {shownPercent(parity.probEven)}%{' '}
                          <Text as="span" color="gray.600">({parity.fairPriceEven.toFixed(3)})</Text>
                        </Td>
                      </Tr>
                    ))}
                </Tbody>
              </Table>
            )}
          </Box>

          <Box p={3} borderWidth={1} borderRadius="md" bg="teal.50">
            <Heading size="xs" mb={1}>Race to N Points</Heading>
            <Text fontSize="xs" color="gray.600" mb={3}>
              A race a player has already reached the target of is left out: who got there first is not
              in the score.
            </Text>
            {result.raceToN.length === 0 ? (
              <Text fontSize="sm" color="gray.600">
                No race is still open at this score.
              </Text>
            ) : (
              <Table variant="simple" size="sm">
                <Thead>
                  <Tr>
                    <Th>Set</Th>
                    <Th>Race to</Th>
                    <Th isNumeric>Player A</Th>
                    <Th isNumeric>Player B</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {[...result.raceToN]
                    .sort((a, b) => a.set - b.set || a.target - b.target)
                    .map(race => (
                      <Tr key={`${race.set}:${race.target}`}>
                        <Td>
                          {race.set}
                          {race.live && (
                            <Badge ml={2} colorScheme="green" fontSize="0.6rem">in progress</Badge>
                          )}
                        </Td>
                        <Td>{race.target}</Td>
                        <Td isNumeric color={leads(race.probA, race.probB) ? 'green.600' : 'red.600'}>
                          {shownPercent(race.probA)}%{' '}
                          <Text as="span" color="gray.600">({race.fairPriceA.toFixed(3)})</Text>
                        </Td>
                        <Td isNumeric color={leads(race.probB, race.probA) ? 'green.600' : 'red.600'}>
                          {shownPercent(race.probB)}%{' '}
                          <Text as="span" color="gray.600">({race.fairPriceB.toFixed(3)})</Text>
                        </Td>
                      </Tr>
                    ))}
                </Tbody>
              </Table>
            )}
          </Box>

          <Box p={3} borderWidth={1} borderRadius="md" bg="teal.50">
            <Heading size="xs" mb={1}>Point N Winner</Heading>
            <Text fontSize="xs" color="gray.600" mb={3}>
              Who wins one numbered point of a set — the only market here settled by a single rally. A
              point the score has already passed is left out: its winner is the rally that was observed,
              not a price. <b>Played</b> is how often the set runs long enough to reach the point at all,
              which is how often the market pays rather than refunds; the two prices beside it are
              conditional on it being played, so they sum to 100% without it.
            </Text>
            {pointNWinner.length === 0 ? (
              <Text fontSize="sm" color="gray.600">
                No numbered point is still to be played at this score — every one offered is either
                behind the score or in a set that can no longer reach it.
              </Text>
            ) : (
              <Table variant="simple" size="sm">
                <Thead>
                  <Tr>
                    <Th>Set</Th>
                    <Th>Point</Th>
                    <Th isNumeric>Played</Th>
                    <Th isNumeric>Player A</Th>
                    <Th isNumeric>Player B</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {[...pointNWinner]
                    .sort((a, b) => a.set - b.set || a.pointNumber - b.pointNumber)
                    .map(point => (
                      <Tr key={`${point.set}:${point.pointNumber}`}>
                        <Td>
                          {point.set}
                          {point.live && (
                            <Badge ml={2} colorScheme="green" fontSize="0.6rem">in progress</Badge>
                          )}
                        </Td>
                        <Td>{point.pointNumber}</Td>
                        {/* Not a side of the pair, so nothing is picked out: it is how often either wins. */}
                        <Td isNumeric color="gray.700">{shownPercent(point.probPlayed)}%</Td>
                        <Td isNumeric color={leads(point.probA, point.probB) ? 'green.600' : 'red.600'}>
                          {shownPercent(point.probA)}%{' '}
                          <Text as="span" color="gray.600">({point.fairPriceA.toFixed(3)})</Text>
                        </Td>
                        <Td isNumeric color={leads(point.probB, point.probA) ? 'green.600' : 'red.600'}>
                          {shownPercent(point.probB)}%{' '}
                          <Text as="span" color="gray.600">({point.fairPriceB.toFixed(3)})</Text>
                        </Td>
                      </Tr>
                    ))}
                </Tbody>
              </Table>
            )}
          </Box>
        </VStack>
      )}
    </Box>
  );
};
